import { Injectable } from '@angular/core';
import Bugsnag from '@bugsnag/js';
import { uniqBy } from 'lodash';
import {
    ContentTypes,
    ErrorMessageHelper,
    FilesHelper,
    ICloudinaryUploadResponse,
    Modal
} from '@lsq-frontend/common-ui';
import { ModalService } from '@lsq-frontend/common-ui';
import { BehaviorSubject, merge, Observable, race, Subject } from 'rxjs';
import {
    DropZoneTarget,
    HttpMethod,
    IUploadRequestOptions,
    Uploader,
    UploaderError
} from 'rxjs-uploader';
import { flatMap, map, scan, takeUntil } from 'rxjs/operators';
import { AnalyticsEvent } from '../../../constants/analytics.constants';
import {
    CreateInvoiceBatchImageRequest,
    CreateInvoiceBatchImageService,
    GetImageUrlRequest,
    GetInvoiceBatchImageUrlService
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import {
    AdditionalUploadType,
    IBatchUploaderData,
    IInvoiceUploadMethodOption,
    InvoiceFileUpload
} from '../invoices.models';
import { InvoiceBatchService } from './invoice-batch.service';
import {
    Guid,
    FileType,
    InvoiceBatchInvoiceType
} from '@lsq-frontend/remote-services/enterprise/common';
import { ErrorResponse } from '@lsq-frontend/angular-shared/api-service';
import { AppSettings } from '@lsq-frontend/fasttrack-shared/environment';
import { AnalyticsService } from '@lsq-frontend/angular-shared/features/analytics';
import { AlertService } from '@lsq-frontend/angular-shared/features/alert-service';

@Injectable({ providedIn: 'root' })
export class UploadInvoicesService {
    // State.
    public invoiceFileUploadsRef: IBatchUploaderData<InvoiceFileUpload[]>;
    public invoiceSummaryFileUploadRef: IBatchUploaderData<InvoiceFileUpload>;
    public invoiceSummaryIsValidSubject = new BehaviorSubject<boolean>(true);
    public resetInvoiceSummaryUploaderSubject = new Subject<any>();
    public resetInvoiceUploaderSubject = new Subject<any>();
    public invoiceFileUploads: InvoiceFileUpload[];
    public invoiceSummaryFileUpload: InvoiceFileUpload;
    public invoiceBatchAdditionalUploadsMap = new Map<Guid, IBatchUploaderData>();
    public invoiceBatchImageAdditionalUploadsMap = new Map<Guid, IBatchUploaderData>();

    // Config.
    public invoiceUploader: Uploader<InvoiceFileUpload>;
    public invoiceSummaryUploader: Uploader<InvoiceFileUpload>;
    public allowedContentTypesForInvoices = ContentTypes.ImagesAndPdfs;
    public allowedContentTypesForSpreadsheets = ContentTypes.Spreadsheets;
    public maxNumberOfInvoiceSummaryUploads = 1;
    public maxNumberOfSpreadsheetUploads = 1;
    public shouldFetchPreview = true;
    public onBeforeInvoiceFileUploadsExecuted: (fileUploads: InvoiceFileUpload[]) => void;

    // Copy.
    public tooManyInvoiceBatchImages = `The number of files within a single submission cannot exceed ${this._appSettings.maxNumberOfFilesPerInvoiceBatch}.`;
    public tooManySpreadsheets =
        'You can only upload a single spreadsheet per invoice submission. Any additional spreadsheets need to be separate submissions.';

    private _tempMasterBatchKey: Guid;
    constructor(
        private _invoiceBatchService: InvoiceBatchService,
        private _alertService: AlertService,
        private _appSettings: AppSettings,
        private _createInvoiceBatchImageService: CreateInvoiceBatchImageService,
        private _getInvoiceBatchImageUrlService: GetInvoiceBatchImageUrlService,
        private _modalService: ModalService,
        private _analyticsService: AnalyticsService,
        private _errorMessageHelper: ErrorMessageHelper
    ) {
        this.resetInvoiceSummaryUploaderSubject.subscribe(() => {
            if (!!this.invoiceSummaryUploader) {
                this.invoiceSummaryUploader.clear();
            }
            this.invoiceSummaryFileUpload = null;
            this.invoiceSummaryFileUploadRef = null;
            this.invoiceSummaryUploader = this._createInvoiceUploader(
                this._appSettings.imageUploadUrl,
                this.allowedContentTypesForInvoices,
                this.maxNumberOfInvoiceSummaryUploads,
                (fileUploads) => this._onBeforeInvoiceFileUploadsExecuted(fileUploads),
                (fileUpload) => this.handleInvoiceSummaryFileUploaded(fileUpload),
                false,
                false,
                null,
                true
            );
            this._initErrorStream();
        });
        this.resetInvoiceUploaderSubject.subscribe(() => {
            if (!!this.invoiceUploader) {
                this.invoiceUploader.clear();
            }
            this.invoiceFileUploads = [];
            this.invoiceFileUploadsRef = null;
            this.invoiceUploader = this._createInvoiceUploader(
                this.uploadUrl,
                this.allowedContentTypes,
                () => this._getInvoiceUploaderFileCountLimit(),
                (fileUploads) => this._onBeforeInvoiceFileUploadsExecuted(fileUploads),
                null,
                this._invoiceBatchService.isSpreadsheetUploadMethodSelected()
                    ? this.tooManySpreadsheets
                    : this.tooManyInvoiceBatchImages
            );
            this._initErrorStream();
        });

        this.reset();
    }

    public reset(): void {
        this._invoiceBatchService.reset();
        this.resetInvoiceUploader();
        this.resetInvoiceSummaryUploader();
        this.invoiceBatchImageAdditionalUploadsMap.clear();
    }

    public resetInvoiceUploader(): void {
        this.resetInvoiceUploaderSubject.next();
    }

    public resetInvoiceSummaryUploader(): void {
        this.resetInvoiceSummaryUploaderSubject.next();
    }

    /**
     * Returns a callback function to be passed to `Uploader#setRequestOptions` based on the provided
     * `url` and optional `parentImageKey`. That callback is invoked before each file is uploaded (but
     * after it's selected).
     */
    public requestOptionsCallbackFactory(
        url: string,
        parentImageKey?: Guid,
        invoiceBatchKey?: Guid,
        isForInvoiceSummary = false
    ): (fileUpload: InvoiceFileUpload) => Promise<IUploadRequestOptions> {
        return async (fileUpload: InvoiceFileUpload): Promise<IUploadRequestOptions> => {
            try {
                const createImageFileRequest = new CreateInvoiceBatchImageRequest({
                    invoiceBatchKey: invoiceBatchKey || this._invoiceBatchService.invoiceBatchKey,
                    fileName: fileUpload.name,
                    fileType: FilesHelper.getFileType(fileUpload.name),
                    invoiceBatchInvoiceType: isForInvoiceSummary
                        ? InvoiceBatchInvoiceType.Summary
                        : InvoiceBatchInvoiceType.Image,
                    parentImageFileKey: parentImageKey || null
                });
                const createImageFileResponse = await this._createInvoiceBatchImageService.executeOnce(
                    createImageFileRequest
                );

                fileUpload.imageKey = createImageFileResponse.invoiceBatchImageKey;

                return {
                    url,
                    method: HttpMethod.Post,
                    // ***** Important *****
                    // DO NOT change data without updating the GetCloudinarySignatureService
                    formData: {
                        angle: 'exif', // This parameter should not be in the backend.
                        api_key: this._appSettings.cloudinaryApiKey,
                        public_id: createImageFileResponse.keyName,
                        signature: createImageFileResponse.signature,
                        tags: createImageFileResponse.tags,
                        timestamp: `${createImageFileResponse.timestamp}`,
                        type: 'authenticated'
                    }
                };
            } catch (error) {
                fileUpload.reject(error);
                Bugsnag.notify(error);
                throw error;
            }
        };
    }

    /**
     * Create a one-off stream of file uploads, with a default configuration equal to that of the
     * `invoiceUploader` (used by `initInvoiceFileUploadsStream`). You may override that configuration
     * by passing a partial config object to the `uploaderConfigOrUploader` param, or use that param
     * to provide your own instance of `Uploader`.
     */
    public executeInvoiceFileUploads(
        uploader: Uploader<InvoiceFileUpload>,
        fileInputElements: HTMLInputElement[] = [],
        dropZoneElements: DropZoneTarget[] = [],
        completedFileUploadsToAddStream?: Observable<InvoiceFileUpload[]>,
        masterInvoiceBatchKey?: Guid,
        type?: AdditionalUploadType
    ): BehaviorSubject<InvoiceFileUpload[]> {
        const invoiceFileUploadsSubject = new BehaviorSubject<InvoiceFileUpload[]>([]);
        const mergeArgs: Observable<InvoiceFileUpload[]>[] = [];
        if (masterInvoiceBatchKey) {
            this._tempMasterBatchKey = masterInvoiceBatchKey;
        }
        mergeArgs.push(
            // Use the `streamFileUploads` method, the highest-level API of `Uploader`.
            uploader.streamFileUploads(...fileInputElements, ...dropZoneElements)
        );
        if (completedFileUploadsToAddStream) {
            // Add any already-uploaded `FileUpload`s to the stream.
            mergeArgs.push(
                completedFileUploadsToAddStream,
                // Make sure to listen to the isMarkedForRemovalStream for the mobileUploads since we created these files manually.
                completedFileUploadsToAddStream.pipe(
                    flatMap((fileUploads) =>
                        merge(
                            ...fileUploads.map((fileUpload) =>
                                fileUpload.isMarkedForRemovalStream.pipe(map(() => fileUploads))
                            )
                        )
                    )
                )
            );
        }

        merge(...mergeArgs)
            .pipe(
                // We are getting fileUploads from 2 sources (the input element and the listInvoiceBatches endpoint).
                // The below logic merges the incoming arrays and removes those that have been deleted.
                scan<InvoiceFileUpload[]>((_previousAccFileUploads, _currentFileUploads) => {
                    let previousAccFileUploads = _previousAccFileUploads.filter(
                        (fileUpload) => !fileUpload.isMarkedForRemoval
                    );

                    // Any of the newest files that are 'markedForRemoval' should also be removed from the previousFileUploads so that when
                    // the merge happens, they aren't added back to the accumulated result.
                    _currentFileUploads.forEach((currentUpload) => {
                        if (currentUpload.isMarkedForRemoval) {
                            previousAccFileUploads = previousAccFileUploads.filter(
                                (previous) => previous.imageKey !== currentUpload.imageKey
                            );
                        }
                    });
                    // Now we can remove those that are markedForRemoval out of the currentFileUploads.
                    const currentFileUploads = _currentFileUploads.filter(
                        (fileUpload: InvoiceFileUpload) => !fileUpload.isMarkedForRemoval
                    );

                    let accFileUploads = [...previousAccFileUploads, ...currentFileUploads];
                    if (type === AdditionalUploadType.InvoiceBatch) {
                        accFileUploads = accFileUploads
                            .map((fileUpload: InvoiceFileUpload) => {
                                if (!fileUpload.masterInvoiceBatchKey) {
                                    fileUpload.masterInvoiceBatchKey = this._tempMasterBatchKey;
                                }
                                return fileUpload;
                            })
                            .filter(
                                (fileUpload: InvoiceFileUpload) =>
                                    fileUpload.masterInvoiceBatchKey === masterInvoiceBatchKey
                            );
                    }

                    // Now that we have merged the arrays, we still need to remove any of the mobileUploads that have been deleted.
                    // They won't follow the normal 'isMarkedForRemoval' constructs because they get recreated on every polling loop.
                    // They are tracked in a separate array, so if we see any of these in that array we should remove them.
                    accFileUploads.filter((accFileUpload: InvoiceFileUpload) => {
                        const isFileInRemovedArray = this._invoiceBatchService.removedMobileUploads.find(
                            (removedUpload) => removedUpload.imageKey === accFileUpload.imageKey
                        );
                        return !isFileInRemovedArray;
                    });

                    // Now remove duplicates (by imageKeys).
                    return uniqBy(accFileUploads, 'imageKey');
                }, [])
            )
            .subscribe((fileUploads) => {
                invoiceFileUploadsSubject.next(fileUploads);
            });

        // Expose the subject, so that a consumer can call `.unsubscribe()`.
        return invoiceFileUploadsSubject;
    }

    /**
     * Pipe invoice file uploads into `invoiceFileUploadsStream` and store the result in `invoiceFileUploads`.
     */
    public initInvoiceFileUploadsStream(
        completedFileUploadsToAddStream?: Observable<InvoiceFileUpload[]>
    ): IBatchUploaderData<InvoiceFileUpload[]> {
        if (!!this.invoiceFileUploadsRef) {
            return this.invoiceFileUploadsRef;
        }

        // No ref was found, so create one. First, remove any existing inputs.
        const fileInputElement = Uploader.createFileInputElement(
            this._invoiceBatchService.isSpreadsheetUploadMethodSelected() ? 'single' : 'multiple',
            this.allowedContentTypes.join(','),
            'upload-invoices-file-input'
        );

        this.invoiceFileUploadsRef = {
            fileUploadsStream: this.executeInvoiceFileUploads(
                this.invoiceUploader,
                [fileInputElement],
                [document],
                completedFileUploadsToAddStream
            )
                .asObservable()
                .pipe(takeUntil(this.resetInvoiceUploaderSubject)),
            inputElement: fileInputElement
        };

        this.invoiceFileUploadsRef.fileUploadsStream.subscribe((fileUploads) => {
            this.invoiceFileUploads = fileUploads;
        });

        return this.invoiceFileUploadsRef;
    }

    /**
     * Pipe a single invoice summary file upload into `invoiceSummaryFileUploadStream` and store
     * the result in `invoiceSummaryFileUpload`.
     */
    public initInvoiceSummaryFileUploadStream(): IBatchUploaderData<InvoiceFileUpload> {
        if (!!this.invoiceSummaryFileUploadRef) {
            return this.invoiceSummaryFileUploadRef;
        }

        // No ref was found, so create one.
        const fileInputElement = Uploader.createFileInputElement(
            'single',
            this.allowedContentTypesForInvoices.join(','),
            'invoice-summary-file-input'
        );
        this.invoiceSummaryFileUploadRef = {
            fileUploadsStream: this.executeInvoiceFileUploads(this.invoiceSummaryUploader, [
                fileInputElement
            ])
                .asObservable()
                .pipe(
                    map((_fileUploads) => (_fileUploads ? _fileUploads[0] : null)),
                    takeUntil(this.resetInvoiceSummaryUploaderSubject)
                ),
            inputElement: fileInputElement
        };

        this.invoiceSummaryFileUploadRef.fileUploadsStream.subscribe((_fileUpload) => {
            this.invoiceSummaryFileUpload = _fileUpload;
            this.invoiceSummaryIsValidSubject.next(
                // It's optional, so a `null` (or removed) fileUpload is valid.
                !_fileUpload ||
                    _fileUpload.isMarkedForRemoval ||
                    // If there is an upload, it's only valid if it's completed.
                    _fileUpload.isUploadStatusComplete
            );
        });

        return this.invoiceSummaryFileUploadRef;
    }

    /**
     * Orchestrates the uploading of additional files to an invoice batch. If necessary, creates a new
     * `InvoiceBatch` that's linked to an existing ("master") batch and adds the files to the new batch.
     * A condition inside of `this.requestOptionsCallbackFactory` ensures that this invoice batch is used,
     * rather than a new one being created immediately before the file upload executes, which is what
     * happens in the regular invoice upload flow.
     */
    public async getOrCreateAdditionalUploads(
        type: AdditionalUploadType,
        masterInvoiceBatchOrParentImageFileKey: Guid,
        invoiceBatchKey?: Guid,
        onFileCountLimitExceeded?: (fileCountLimit: number) => void,
        completedFileUploadsToAddStream?: Observable<InvoiceFileUpload[]>
    ): Promise<IBatchUploaderData> {
        let parentImageFileKey: Guid;
        let masterInvoiceBatchKey: Guid;
        const newInvoiceBatchKeys = new Set<Guid>();
        this._tempMasterBatchKey = masterInvoiceBatchOrParentImageFileKey;
        // Look for an existing ref.
        if (type === AdditionalUploadType.InvoiceBatch) {
            masterInvoiceBatchKey = masterInvoiceBatchOrParentImageFileKey;
            const uploaderData = this.invoiceBatchAdditionalUploadsMap.get(masterInvoiceBatchKey);
            const lastInvoiceBatchKey =
                !!uploaderData && Array.from(uploaderData.invoiceBatchKeys).pop();
            const creatingNewInvoiceBatch = !invoiceBatchKey && !lastInvoiceBatchKey;

            if (creatingNewInvoiceBatch) {
                invoiceBatchKey = await this._invoiceBatchService.createInvoiceBatch(
                    masterInvoiceBatchKey
                );
                newInvoiceBatchKeys.add(invoiceBatchKey);
            } else if (!invoiceBatchKey && !!lastInvoiceBatchKey) {
                invoiceBatchKey = lastInvoiceBatchKey;
            }

            if (!!uploaderData) {
                uploaderData.invoiceBatchKeys.add(invoiceBatchKey);
                this.invoiceBatchAdditionalUploadsMap.set(masterInvoiceBatchKey, uploaderData);
                return this.invoiceBatchAdditionalUploadsMap.get(masterInvoiceBatchKey);
            }
        }
        if (type === AdditionalUploadType.InvoiceBatchImage) {
            parentImageFileKey = masterInvoiceBatchOrParentImageFileKey;
            if (
                this.invoiceBatchImageAdditionalUploadsMap.has(
                    masterInvoiceBatchOrParentImageFileKey
                )
            ) {
                return this.invoiceBatchImageAdditionalUploadsMap.get(parentImageFileKey);
            }
        }

        // No ref was found, so create one.
        const fileInputElement = Uploader.createFileInputElement(
            'multiple',
            this.allowedContentTypesForInvoices.join(','),
            'additional-upload-file-input'
        );
        const uploader = this._createInvoiceUploader(
            [this._appSettings.imageUploadUrl, null, invoiceBatchKey],
            this.allowedContentTypesForInvoices,
            this._appSettings.maxNumberOfFilesPerInvoiceBatch,
            null,
            null,
            null,
            true,
            onFileCountLimitExceeded
        );
        uploader.errorStream.subscribe((error) => {
            if (typeof error.message === 'string') {
                this._alertService.error(error.message);
            }
            Bugsnag.notify(error);
        });
        const additionalUploadsRef: IBatchUploaderData = {
            fileUploadsStream: this.executeInvoiceFileUploads(
                uploader,
                [fileInputElement],
                [],
                completedFileUploadsToAddStream,
                masterInvoiceBatchOrParentImageFileKey,
                type
            ),
            inputElement: fileInputElement
        };

        // Insert it into the correct map, and return it.
        if (type === AdditionalUploadType.InvoiceBatch) {
            this.invoiceBatchAdditionalUploadsMap.set(masterInvoiceBatchKey, {
                ...additionalUploadsRef,
                invoiceBatchKeys: newInvoiceBatchKeys
            });
            return this.invoiceBatchAdditionalUploadsMap.get(masterInvoiceBatchKey);
        }
        if (type === AdditionalUploadType.InvoiceBatchImage) {
            this.invoiceBatchImageAdditionalUploadsMap.set(
                parentImageFileKey,
                additionalUploadsRef
            );
            return this.invoiceBatchImageAdditionalUploadsMap.get(parentImageFileKey);
        }
    }

    public selectUploadMethodOption(uploadMethodOption: IInvoiceUploadMethodOption): void {
        this._invoiceBatchService.selectUploadMethodOption(uploadMethodOption);
        // The `invoiceUploader` should be configured differently depending on whether the user is
        // uploading invoice images or a spreadsheet.
        if (this._invoiceBatchService.isSpreadsheetUploadMethodSelected()) {
            this.invoiceUploader.patchRequestOptions({
                url: this._appSettings.fileUploadUrl
            });
            this.invoiceUploader.setAllowedContentTypes(this.allowedContentTypesForSpreadsheets);
            this.invoiceUploader.setFileCountLimit(this.maxNumberOfSpreadsheetUploads);
        } else {
            this.invoiceUploader.patchRequestOptions({
                url: this._appSettings.imageUploadUrl
            });
            this.invoiceUploader.setAllowedContentTypes(this.allowedContentTypesForInvoices);
            this.invoiceUploader.setFileCountLimit(
                this._appSettings.maxNumberOfFilesPerInvoiceBatch
            );
        }
    }

    public async submitInvoiceBatch(
        invoiceFileUploads?: InvoiceFileUpload[],
        invoiceBatchKey?: Guid,
        isLinkedBatch?: boolean
    ): Promise<number> {
        // We pass in `invoiceFileUploads` because the order of that array is handled in the component.
        const invoiceBatchImageKeys = [];

        if (!!this.invoiceSummaryFileUpload) {
            invoiceBatchImageKeys.push(this.invoiceSummaryFileUpload.imageKey);
        }

        invoiceFileUploads
            .filter((fileUpload) => fileUpload.isUploadStatusComplete)
            .forEach((fileUpload) => {
                invoiceBatchImageKeys.push(fileUpload.imageKey);

                // Add any linked files.
                fileUpload.linkedFileUploads
                    .filter((linkedFileUpload) => linkedFileUpload.isUploadStatusComplete)
                    .forEach((linkedFileUpload) => {
                        invoiceBatchImageKeys.push(linkedFileUpload.imageKey);
                    });
            });

        this._analyticsService.triggerEventWithPayload(AnalyticsEvent.InvoiceFilesUploaded, {
            count: invoiceBatchImageKeys.length
        });

        await this._invoiceBatchService.submit(
            invoiceBatchImageKeys,
            invoiceBatchKey,
            isLinkedBatch
        );

        return invoiceBatchImageKeys.length;
    }

    // Event handlers.
    public async handleInvoiceFileUploaded(
        fileUpload: InvoiceFileUpload,
        isForAdditionalUploads?: boolean
    ): Promise<InvoiceFileUpload> {
        return this._handleFileUploaded(fileUpload, isForAdditionalUploads);
    }

    public async handleInvoiceSummaryFileUploaded(
        fileUpload: InvoiceFileUpload
    ): Promise<InvoiceFileUpload> {
        return this._handleFileUploaded(fileUpload, true);
    }

    // Public getters for convenience.
    public get uploadUrl(): string {
        if (this._invoiceBatchService.isSpreadsheetUploadMethodSelected()) {
            return this._appSettings.fileUploadUrl;
        }
        return this._appSettings.imageUploadUrl;
    }

    public get additionalUploadUrl(): string {
        return this._appSettings.imageUploadUrl;
    }

    public get invoicesUploadZoneDraggedOverStream(): Observable<boolean> {
        return this.invoiceUploader.isDraggedOverStream;
    }

    public get allowedContentTypes(): string[] {
        if (this._invoiceBatchService.isSpreadsheetUploadMethodSelected()) {
            return this.allowedContentTypesForSpreadsheets;
        } else {
            return this.allowedContentTypesForInvoices;
        }
    }

    // Private methods.
    private _createInvoiceUploader(
        urlOrCallbackFactoryArgs: string | (string | Guid)[],
        allowedContentTypes: string[] = ContentTypes.ImagesAndPdfs,
        fileCountLimit?: number | (() => number),
        allFilesQueuedCallback?: (fileUploads: InvoiceFileUpload[]) => Promise<InvoiceFileUpload[]>,
        fileUploadedCallback?: (fileUpload: InvoiceFileUpload) => Promise<InvoiceFileUpload>,
        onFileCountLimitExceededMessage: boolean | string = true,
        isForAdditionalUploads = false,
        handleFileCountLimitExceeded?: (fileCountLimit: number) => void,
        isForInvoiceSummary = false
    ): Uploader<InvoiceFileUpload> {
        let requestOptionsCallback: (
            fileUpload: InvoiceFileUpload
        ) => Promise<IUploadRequestOptions>;
        let onFileCountLimitExceeded = handleFileCountLimitExceeded;
        if (typeof urlOrCallbackFactoryArgs === 'string') {
            requestOptionsCallback = this.requestOptionsCallbackFactory(
                urlOrCallbackFactoryArgs,
                null,
                null,
                isForInvoiceSummary
            );
        } else {
            requestOptionsCallback = this.requestOptionsCallbackFactory(
                urlOrCallbackFactoryArgs[0],
                urlOrCallbackFactoryArgs[1],
                urlOrCallbackFactoryArgs[2],
                isForInvoiceSummary
            );
        }

        if (typeof fileCountLimit !== 'number') {
            fileCountLimit = () => this._getInvoiceUploaderFileCountLimit();
        }

        if (typeof fileUploadedCallback !== 'function') {
            fileUploadedCallback = (fileUpload) =>
                this.handleInvoiceFileUploaded(fileUpload, isForAdditionalUploads);
        }

        if (!onFileCountLimitExceeded) {
            if (typeof onFileCountLimitExceededMessage === 'string') {
                onFileCountLimitExceeded = () =>
                    this._showMaxFileUploadsModal(onFileCountLimitExceededMessage);
            } else if (onFileCountLimitExceededMessage) {
                onFileCountLimitExceeded = () =>
                    this._showMaxFileUploadsModal(this.tooManyInvoiceBatchImages);
            }
        }

        let disallowedContentTypeErrorMessage: (file: any) => string;
        if (allowedContentTypes === ContentTypes.ImagesAndPdfs) {
            disallowedContentTypeErrorMessage = (file) =>
                `${file.name} is an unsupported file type; pdf, tiff, jpg, and png are supported.`;
        } else if (allowedContentTypes === ContentTypes.Spreadsheets) {
            disallowedContentTypeErrorMessage = (file) =>
                `${file.name} is an unsupported file type; csv, xlsx, and xls are supported.`;
        }

        return new Uploader<InvoiceFileUpload>({
            fileUploadType: InvoiceFileUpload,
            allFilesQueuedCallback,
            fileUploadedCallback,
            allowedContentTypes,
            fileCountLimit,
            fileSizeLimitMb: this._appSettings.fileUploadMaximumSizeMb,
            requestOptions: requestOptionsCallback,
            onFileCountLimitExceeded,
            dragAndDropFlagSelector: '#lsq-upload-invoices-zone',
            disallowedContentTypeErrorMessage
        });
    }

    private async _handleFileUploaded(
        fileUpload: InvoiceFileUpload,
        isInvoiceSummaryOrAdditionalUpload = false
    ): Promise<InvoiceFileUpload> {
        const response: ICloudinaryUploadResponse = fileUpload.responseBody;

        if (
            (this._invoiceBatchService.isSpreadsheetUploadMethodSelected() &&
                !isInvoiceSummaryOrAdditionalUpload) ||
            !this.shouldFetchPreview
        ) {
            fileUpload.previewUrl = null;
        } else {
            const getInvoiceBatchImageUrlRequest = new GetImageUrlRequest();
            getInvoiceBatchImageUrlRequest.imageKey = fileUpload.imageKey;
            getInvoiceBatchImageUrlRequest.width = 330;
            getInvoiceBatchImageUrlRequest.format = FileType.Jpg;
            getInvoiceBatchImageUrlRequest.page = 1;

            try {
                const getImageUrlResponse = await this._getInvoiceBatchImageUrlService.executeOnce(
                    getInvoiceBatchImageUrlRequest
                );
                fileUpload.previewUrl = getImageUrlResponse.url;
                fileUpload.imageId = response.public_id;
                fileUpload.url = response.secure_url;
                fileUpload.pages = response.pages || 1;
                fileUpload.masterInvoiceBatchKey = this._tempMasterBatchKey;
            } catch (getImageUrlError) {
                fileUpload.reject();
                Bugsnag.notify(
                    new Error(
                        `Error happens when getImageUrl: ${JSON.stringify(
                            getImageUrlError
                        )},  ${JSON.stringify(fileUpload)}`
                    )
                );
            }
        }

        if (fileUpload.hasFailed) {
            this._invoiceBatchService.reportFailedUpload(fileUpload);
            Bugsnag.notify(
                new Error(
                    `Failed to upload invoices.
                    Response code: ${fileUpload.responseCode},
                    ResponseBody: ${fileUpload.responseBody}.`
                )
            );
        } else {
            await this._invoiceBatchService.reportCompletedUpload(fileUpload);
        }

        return fileUpload;
    }

    private async _onBeforeInvoiceFileUploadsExecuted(
        fileUploads: InvoiceFileUpload[]
    ): Promise<InvoiceFileUpload[]> {
        await this._invoiceBatchService
            .findOrCreateInvoiceBatch()
            .toPromise()
            .catch((error) => {
                // throwing error or not catching the error will cause the FileUpload spinning forever.
                // todo: need a separate story to figure out the workflow how to handle when invoice batch is failed
                // it does not make sense to continue CreateInvoiceBatchImage or upload to cloudinary if the invoicebatch record is failed to create.
                // throw e;
                fileUploads.forEach((f) => f.reject(error));
                this._alertService.error(this._errorMessageHelper.GENERIC_ERROR);
            });

        if (this.onBeforeInvoiceFileUploadsExecuted) {
            // If callback has been registered, call it.
            this.onBeforeInvoiceFileUploadsExecuted(fileUploads);
        }
        return fileUploads;
    }

    private _showMaxFileUploadsModal(bodyText: string): void {
        this._modalService.showModal(
            new Modal({
                id: 'max-uploads-alert',
                title: 'Too many files',
                bodyText,
                showFooter: true,
                showOkButton: true,
                okButtonText: 'Got it',
                showCancelButton: false
            })
        );
    }

    private _getInvoiceUploaderFileCountLimit(): number {
        if (this._invoiceBatchService.isSpreadsheetUploadMethodSelected()) {
            return this.maxNumberOfSpreadsheetUploads;
        } else {
            return this._appSettings.maxNumberOfFilesPerInvoiceBatch;
        }
    }

    private _initErrorStream(): void {
        // Listen for errors and display them as alerts.
        const mergeArgs: Observable<ErrorResponse | UploaderError>[] = [];
        if (this.invoiceUploader) {
            mergeArgs.push(this.invoiceUploader.errorStream);
        }
        if (this.invoiceSummaryUploader) {
            mergeArgs.push(this.invoiceSummaryUploader.errorStream);
        }

        merge(...mergeArgs)
            .pipe(
                takeUntil(
                    race(this.resetInvoiceUploaderSubject, this.resetInvoiceSummaryUploaderSubject)
                )
            )
            .subscribe((error) => {
                if (typeof error.message === 'string') {
                    this._alertService.error(error.message);
                }
                Bugsnag.notify(error);
            });
    }
}

// This abstraction is needed to support IE and Edge, which do not support calling
// `new File()`.
export class FakeFile {
    // `FakeFile extends Blob` is a better pattern, but throws a TypeError in Chrome.
    constructor(public name: string) {
        const blob = new Blob() as any;
        blob.name = name;
        return blob;
    }
}
